// src/utils/copyMedicines.js
//
// "Copy medicines into this prescription" from a previous signed prescription.
// Adds rows to the current draft; never changes or removes an existing row.
// Duplicates are detected by drug name (case/space-insensitive) — the same drug
// at a different strength is still a duplicate, because the doctor should look
// at it rather than silently get two of the same medicine.

import { MAX_ITEMS, emptyItem, itemsFromPrescription } from './prescription';

const nameKey = s => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * @param {Array} existing   current builder rows
 * @param {object|Array} source  a Prescription ({items}) or an array of server items
 * @returns {{items: Array, added: number, duplicates: number, overLimit: number}}
 */
export function mergeCopiedMedicines(existing, source, max = MAX_ITEMS) {
  const current = Array.isArray(existing) ? existing : [];
  const incoming = Array.isArray(source) ? itemsFromPrescription({ items: source }) : itemsFromPrescription(source);
  const seen = new Set(current.map(i => nameKey(i.drugName)).filter(Boolean));
  const items = [...current];
  let added = 0; let duplicates = 0; let overLimit = 0;
  incoming.forEach(row => {
    const k = nameKey(row.drugName);
    if (!k) return;
    if (seen.has(k)) { duplicates += 1; return; }
    if (items.length >= max) { overLimit += 1; return; }
    seen.add(k);
    const { key, ...fields } = row; // a fresh React key, never the source row's
    items.push(emptyItem(fields));
    added += 1;
  });
  return { items, added, duplicates, overLimit };
}

/** Toast text for a merge result. */
export function copyMedicinesMessage({ added, duplicates, overLimit }) {
  if (added === 0) {
    if (overLimit > 0) return `Nothing added — the prescription already has the maximum of ${MAX_ITEMS} medicines`;
    return duplicates > 0 ? 'Nothing added — those medicines are already in this prescription' : 'That prescription has no medicines to copy';
  }
  const base = `Added ${added} medicine${added === 1 ? '' : 's'} to this prescription`;
  const skipped = [];
  if (duplicates) skipped.push(`${duplicates} already there`);
  if (overLimit) skipped.push(`${overLimit} over the ${MAX_ITEMS}-medicine limit`);
  return skipped.length ? `${base} (${skipped.join(', ')})` : base;
}
